import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import kebabCase from "lodash/kebabCase";
import { Typeahead } from "react-bootstrap-typeahead";

import useQuery from "../../../hooks/useQuery";
import useUser from "../../../hooks/useUser";

const Financiers = (props) => {
  const $typeahead = useRef();
  const { t } = useTranslation();
  const user = useUser();

  const [query, setQuery] = useState("");

  const { data, isLoading, isError } = useQuery(
    `catalogs/pms_financiers?q=${query}&locale=${user.state.language}&country=${user.state.country}&v=3`,
    {
      initialData: [],
    }
  );

  const [state, setState] = useState({});
  // Card 872 (pedido 2026-10-01): se salió del input con un texto que no es una
  // cobertura. Queda marcado (y el form no guarda) hasta que lo modifique.
  const [rejected, setRejected] = useState(false);

  // `setCustomValidity`: App/Form/Form.js no llama a onSubmit si el form no pasa
  // `checkValidity()`, y `reportValidity()` le muestra este mensaje. También con
  // `warning` (fila con número de afiliado y sin cobertura): si no, se marcaba
  // pero se guardaba igual (QA 2026-10-01).
  const invalid = rejected || !!props.warning;
  useEffect(() => {
    const input = $typeahead.current?.getInput?.();
    input?.setCustomValidity?.(
      invalid ? t("financier_not_in_catalog", "Elegí una cobertura del listado.") : ""
    );
  }, [invalid]);

  const onChange = (array) => {
    if (props.clearOnSelect) {
      $typeahead.current.clear();
    }
    const item = JSON.clone(array[0]);
    if (!item) return;
    const isCustom = !!(item.customOption || /^new-id-/.test(item.id || "-----"));
    if (item && item.label && isCustom) {
      item.id = `pms_financiers:${kebabCase(item.label)}`;
    }
    item.customOption = isCustom;
    item.deleted = false;
    item.locale = item.locale || user.state.language || "es-AR";
    item.label = t(item.id, item.label);
    setRejected(false);
    setState({ ...item });
    props.onChange({ ...item });
  };

  const onSearch = (query) => {
    setRejected(false);
    setState({});
    setQuery(query);
  };

  // Bug real (auditoría 2026-08-14): accedía a props.value.id/.label sin
  // guardas — si props.value venía undefined/null (p.ej. una cobertura
  // borrada/limpiada, mismo tipo de caso ya visto y arreglado en
  // EHR/V2/Prescription/Consumer.js), esto tiraba
  // "Cannot read properties of undefined".
  const restoreValue = () => {
    const value = props.value || {};
    setState({ ...value, label: t(value.id, value.label) });
  };

  // Por contenido y no por referencia: el form clona sus datos en cada cambio, y
  // con `[props.value]` cualquier otro input resincronizaba y pisaba el texto
  // escrito con la cobertura guardada.
  // Si cambia la cobertura que recibe (otra elegida, o se borró una fila y ésta
  // pasó a mostrar la siguiente), la marca de error de antes ya no aplica.
  useEffect(() => {
    setRejected(false);
    restoreValue();
  }, [props.value?.id, props.value?.label]);

  // Card 872 (corrección de QA, 2026-09-29; pedido de Francisco 2026-10-01): el
  // texto tipeado nunca llega al form (`props.onChange` corre sólo al elegir una
  // opción). Al salir sin elegir, el texto queda escrito y marcado (`rejected`)
  // hasta que lo modifique, y el form no guarda (ver `setCustomValidity`).
  // Con el campo vacío no se marca: vuelve a la cobertura guardada, si había.
  const onBlur = (e) => {
    if (state.label) return;
    if (e?.target?.value?.trim()) return setRejected(true);
    if (props.value?.id || props.value?.label) restoreValue();
  };

  const options = data.filter(
    (item) => props.any !== false || item.id.substr(-1) !== "/"
  );

  // Lo tipeado no es una cobertura del catálogo: el menú ofrece crearla.
  const canCreate = (text) =>
    !isLoading && !options.some((d) => d.id === `pms_financiers:${kebabCase(text)}`);

  // Card 872 — Enter sin ningún elemento resaltado (con uno resaltado lo elige
  // el typeahead, que corre antes que este handler):
  // - una única opción del catálogo → se selecciona;
  // - más de una, o una más la de crear → se resalta la primera.
  // Contar la de crear evita que Enter elija la única sugerencia del buscador
  // (que es difuso) cuando se quería cargar una cobertura nueva.
  // Sólo con la búsqueda ya resuelta para lo tipeado: si no, `data` es la de
  // la búsqueda anterior.
  const onKeyDown = (e) => {
    if (e.key !== "Enter" || e.target.getAttribute("aria-activedescendant")) return;
    const text = e.target.value;
    if (isLoading || query !== text || text.trim().length < 2) return;
    const total = options.length + (canCreate(text) ? 1 : 0);
    if (total === 0) return;
    const instance = $typeahead.current;
    if (total === 1 && options.length === 1) {
      onChange([options[0]]);
      instance?.hideMenu?.();
    } else {
      // Lo mismo que hace la flecha abajo. `_handleActiveIndexChange` es de la
      // instancia de react-bootstrap-typeahead 5.x (no hay API pública).
      instance?._handleActiveIndexChange?.(0);
    }
  };

  // Bug real (auditoría 2026-08-14): emptyLabel no chequeaba isError — si
  // la búsqueda fallaba, data quedaba en initialData: [] y esto se veía
  // idéntico a "no hay coincidencias", sin ninguna pista de que en
  // realidad falló el request.
  return (
    <Typeahead
      selected={[state].filter((p) => p.label)}
      isLoading={!!isLoading}
      ref={$typeahead}
      shouldSelect
      inputProps={{
        id: props.inputID,
        // Mismo resaltado que el número de afiliado en Consumers/Form.
        className: invalid ? "border-danger bg-danger-light focus-ring-danger" : undefined,
      }}
      minLength={2}
      filterBy={() => true}
      id={["pms_financiers", state.id].filter(Boolean).join("_")}
      allowNew={() => canCreate(query)}
      onChange={onChange}
      onInputChange={onSearch}
      onKeyDown={onKeyDown}
      onBlur={onBlur}
      options={options}
      placeholder={t(`financier_query_placeholder`)}
      newSelectionPrefix={`${t("new_financier", "Nueva cobertura")}: `}
      emptyLabel={isError ? t(`error`) : isLoading ? t(`searching`) : t(`no_matches`)}
      paginationText={t(`display_more_results`)}
      promptText={t(`type_to_search`)}
      searchText={t(`searching`)}
    />
  );
};

export default Financiers;
